/**
 * Lusuku Product Video Upload Utility — Main Dashboard
 * ─────────────────────────────────────────────────────────────────────────
 * Centralised VIDEO pipeline for the product forms, mirroring the frozen
 * product-video contract shared with the server sanitizer
 * (Lusuku_Dashboard_Node-Server/utils/productVideos.js) and the mobile app:
 *
 *   videos: [                   // max 3 per product
 *     { kind: "CLOUDINARY" | "YOUTUBE",
 *       url: String,            // Cloudinary secure_url OR canonical
 *                               // YouTube watch URL
 *       publicId: String,       // CLOUDINARY only; "" for YOUTUBE
 *       thumbnailUrl: String,   // CLOUDINARY: url with "/upload/" ->
 *                               //   "/upload/so_0/" and extension -> .jpg
 *                               // YOUTUBE:
 *                               //   https://img.youtube.com/vi/<id>/hqdefault.jpg
 *       durationSec: Number }   // 0 when unknown (YouTube)
 *   ]
 *
 * Response-shape conventions follow imageUpload.js: the uploader resolves a
 * plain object with snake_case Cloudinary fields (secure_url, public_id, …).
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ██ LOUD OPERATIONAL WARNING — READ BEFORE DEBUGGING FAILED UPLOADS ██
 *
 * This posts UNSIGNED uploads to the /video/upload endpoint using the
 * dedicated "lusuku-video" preset (created 2026-08-16: Unsigned, folder
 * lusuku-product-videos, allowed formats mp4/mov/webm, NO incoming or
 * eager transformations). Do NOT point this at "lusuku-clou" — that
 * preset carries image eager transforms (c_fill,g_auto,...) and g_auto
 * is unsupported for video, so video uploads through it can fail.
 * If the preset is renamed or deleted in the Cloudinary console
 * (Settings → Upload → Upload presets), uploads break with a preset
 * error. This is an ACCOUNT-LEVEL Cloudinary setting — the same
 * class of console-side configuration that caused the 2026-08 PDF-delivery
 * incident. If Cloudinary answers with an error mentioning the preset or
 * the resource type, NOTHING in this code is broken: an operator has to
 * flip the setting in the Cloudinary console. The error mapping below
 * surfaces exactly that message to whoever is looking at the screen.
 * ═══════════════════════════════════════════════════════════════════════
 */

// ── Cloudinary config ──
const CLOUD_NAME = "lusuku";
export const VIDEO_UPLOAD_PRESET = "lusuku-video";
const VIDEO_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/video/upload`;

// ── Frozen contract constants (client-enforced; server clamps too) ──
export const MAX_PRODUCT_VIDEOS = 3;
export const MAX_VIDEO_SIZE_MB = 50;
export const MAX_VIDEO_SIZE_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;
/* Advisory only — vendors get a WARNING above this, never a block. */
export const ADVISORY_MAX_DURATION_SEC = 60;

const ALLOWED_VIDEO_TYPES = [
    "video/mp4",
    "video/quicktime", // .mov
    "video/webm",
];
const ALLOWED_VIDEO_EXTENSIONS = [".mp4", ".mov", ".webm"];

const hasAllowedExtension = (name) => {
    const lower = String(name || "").toLowerCase();
    return ALLOWED_VIDEO_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

/**
 * Validate a video File before upload.
 * @param {File} file
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateVideoFile = (file) => {
    if (!file) {
        return { valid: false, error: "No video file selected." };
    }

    /* Some browsers report an empty MIME type for .mov files, so fall back
       to the extension before rejecting. */
    const typeOk = ALLOWED_VIDEO_TYPES.includes(file.type);
    const extOk = hasAllowedExtension(file.name);
    if (!typeOk && !extOk) {
        return {
            valid: false,
            error: "Unsupported video format. Please use MP4, MOV, or WebM.",
        };
    }

    if (file.size > MAX_VIDEO_SIZE_BYTES) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        return {
            valid: false,
            error: `Video is too large (${sizeMB}MB). Maximum allowed is ${MAX_VIDEO_SIZE_MB}MB.`,
        };
    }

    return { valid: true };
};

/**
 * Read a video file's duration (seconds) via a temporary <video> element.
 * Resolves 0 when the duration cannot be determined — duration is advisory
 * in the contract, so failure here must never block an upload.
 * @param {File} file
 * @returns {Promise<number>}
 */
export const readVideoDuration = (file) =>
    new Promise((resolve) => {
        let objectUrl;
        try {
            objectUrl = URL.createObjectURL(file);
        } catch {
            resolve(0);
            return;
        }
        const video = document.createElement("video");
        video.preload = "metadata";
        video.muted = true;
        let settled = false;
        const finish = (seconds) => {
            if (settled) return;
            settled = true;
            URL.revokeObjectURL(objectUrl);
            video.removeAttribute("src");
            try {
                video.load();
            } catch {
                // Nothing to clean up beyond the revoked URL.
            }
            resolve(seconds);
        };
        const timer = setTimeout(() => finish(0), 10000);
        video.onloadedmetadata = () => {
            clearTimeout(timer);
            const d = Number(video.duration);
            finish(Number.isFinite(d) && d > 0 ? d : 0);
        };
        video.onerror = () => {
            clearTimeout(timer);
            finish(0);
        };
        video.src = objectUrl;
    });

/* Does a Cloudinary error message smell like the preset/resource-type
   console misconfiguration described in the header comment? */
const looksLikePresetProblem = (message) =>
    /upload[\s_-]?preset|resource[\s_-]?type|whitelist|not\s+allowed|disabled\s+for\s+unsigned/i.test(
        String(message || "")
    );

const PRESET_OPERATOR_MESSAGE =
    `Cloudinary refused this video upload. The unsigned upload preset ` +
    `"${VIDEO_UPLOAD_PRESET}" must allow the VIDEO resource type — an operator ` +
    `has to enable it in the Cloudinary console (Settings > Upload > Upload ` +
    `presets > ${VIDEO_UPLOAD_PRESET}). This is the same class of account ` +
    `setting as the 2026-08 PDF-delivery incident; no dashboard code change ` +
    `will fix it.`;

/**
 * Upload a product video to Cloudinary (unsigned, direct from the browser).
 *
 * @param {File}     file                 - Raw File from <input type="file">
 * @param {object}   [options]
 * @param {Function} [options.onProgress] - Progress callback (0-100)
 * @param {string}   [options.preset]     - Upload preset (default VIDEO_UPLOAD_PRESET)
 * @returns {Promise<{ secure_url, public_id, format, bytes, duration,
 *                     durationSec, durationWarning }>}
 *   durationSec is the client-measured duration (0 when unknown);
 *   durationWarning is a non-blocking advisory string, or "" when within
 *   the ADVISORY_MAX_DURATION_SEC budget.
 */
export const uploadVideoToCloudinary = async (file, options = {}) => {
    const { onProgress, preset = VIDEO_UPLOAD_PRESET } = options;

    const validation = validateVideoFile(file);
    if (!validation.valid) {
        throw new Error(validation.error);
    }

    /* Advisory duration check — measured before the upload so the warning
       can be shown regardless of what Cloudinary reports back. */
    const measuredDuration = await readVideoDuration(file);
    const durationWarning =
        measuredDuration > ADVISORY_MAX_DURATION_SEC
            ? `This video is about ${Math.round(measuredDuration)}s long. Videos ` +
              `over ${ADVISORY_MAX_DURATION_SEC}s hold shoppers' attention poorly — ` +
              `consider trimming it. It was uploaded anyway.`
            : "";

    const formData = new FormData();
    formData.append("file", file, file.name || "product-video.mp4");
    formData.append("upload_preset", preset);

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", VIDEO_UPLOAD_URL, true);

        if (onProgress) {
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    onProgress(Math.round((e.loaded / e.total) * 100));
                }
            };
        }

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const data = JSON.parse(xhr.responseText);
                    const cloudDuration = Number(data.duration);
                    resolve({
                        secure_url: data.secure_url,
                        public_id: data.public_id,
                        format: data.format,
                        bytes: data.bytes,
                        duration: Number.isFinite(cloudDuration) ? cloudDuration : 0,
                        durationSec:
                            Number.isFinite(cloudDuration) && cloudDuration > 0
                                ? cloudDuration
                                : measuredDuration,
                        durationWarning,
                    });
                } catch {
                    reject(new Error("Invalid response from video server."));
                }
            } else {
                let raw = `Video upload failed (${xhr.status}). Please try again.`;
                try {
                    const payload = JSON.parse(xhr.responseText || "{}");
                    raw = payload?.error?.message || payload?.message || raw;
                } catch {
                    // Keep the status-based fallback.
                }
                /* Preset / resource-type misconfiguration: tell the operator
                   EXACTLY what to fix — see the loud header comment. */
                if (looksLikePresetProblem(raw)) {
                    reject(new Error(`${PRESET_OPERATOR_MESSAGE} (Cloudinary said: ${raw})`));
                    return;
                }
                reject(new Error(raw));
            }
        };

        xhr.onerror = () =>
            reject(new Error("Network error during video upload. Check your connection."));
        xhr.ontimeout = () =>
            reject(new Error("Video upload timed out. Try a smaller or shorter video."));

        /* 50 MB over a slow uplink is minutes, not the 60s the image path
           allows. */
        xhr.timeout = 10 * 60 * 1000;
        xhr.send(formData);
    });
};

// ── Frozen-contract entry builders ──────────────────────────────────────

/* Extract the 11-char YouTube video id. MUST accept the watch?v=,
   youtu.be/, shorts/ and embed/ forms (plus live/) — mirror of the server
   sanitizer's extractYouTubeId. Returns null when unrecognisable. */
export const extractYouTubeId = (url) => {
    if (typeof url !== "string" || !url.trim()) return null;
    let parsed;
    try {
        parsed = new URL(url.trim());
    } catch {
        return null;
    }
    const idPattern = /^[A-Za-z0-9_-]{11}$/;
    const host = parsed.hostname.toLowerCase().replace(/^(www\.|m\.)/, "");

    if (host === "youtu.be") {
        const id = parsed.pathname.split("/").filter(Boolean)[0] || "";
        return idPattern.test(id) ? id : null;
    }

    if (host === "youtube.com" || host.endsWith(".youtube.com")) {
        const v = parsed.searchParams.get("v");
        if (v && idPattern.test(v)) return v;
        const segments = parsed.pathname.split("/").filter(Boolean);
        if (
            ["shorts", "embed", "live"].includes(segments[0]) &&
            segments[1] &&
            idPattern.test(segments[1])
        ) {
            return segments[1];
        }
    }

    return null;
};

/* ── Cloudinary delivery ──────────────────────────────────────────────
   Cloudinary serves whatever container and codec was UPLOADED unless the
   url asks otherwise. A .mov from an iPhone (QuickTime, usually HEVC)
   plays in Safari and is a silent black box in Chrome and in Android's
   WebView — which is exactly the "it uploaded fine but does not play"
   report. So the stored url asks for an H.264 mp4: `q_auto,vc_auto` after
   /video/upload/ and the extension forced to .mp4. Idempotent; leaves
   non-Cloudinary urls and urls that already carry a transformation alone.
   MIRROR of the server's utils/productVideos.js — keep the two identical. */
export const CLOUDINARY_VIDEO_DELIVERY = "q_auto,vc_auto";
const CLOUDINARY_UPLOAD_MARKER = "/video/upload/";

export const playableCloudinaryVideoUrl = (url) => {
    if (typeof url !== "string") return url;
    if (!/^https:\/\/res\.cloudinary\.com\//i.test(url)) return url;
    const at = url.indexOf(CLOUDINARY_UPLOAD_MARKER);
    if (at < 0) return url;
    const head = url.slice(0, at + CLOUDINARY_UPLOAD_MARKER.length);
    let rest = url.slice(at + CLOUDINARY_UPLOAD_MARKER.length);
    const firstSegment = rest.split("/")[0] || "";
    const isVersion = /^v\d+$/.test(firstSegment);
    const hasTransformation = !isVersion && /^[a-z]{1,3}_/i.test(firstSegment);
    if (!hasTransformation) rest = `${CLOUDINARY_VIDEO_DELIVERY}/${rest}`;
    const m = rest.match(/^([^?#]*)([?#].*)?$/);
    let path = m ? m[1] : rest;
    const suffix = m && m[2] ? m[2] : "";
    path = /\.[a-z0-9]{2,5}$/i.test(path)
        ? path.replace(/\.[a-z0-9]{2,5}$/i, ".mp4")
        : `${path}.mp4`;
    return `${head}${path}${suffix}`;
};

/* The upload url as Cloudinary returned it — the delivery component
   removed — which is what the still-frame thumbnail is derived from
   (a `vc_` parameter on a jpg output is a Cloudinary error). */
const rawCloudinaryVideoUrl = (url) =>
    String(url || "").replace(
        `${CLOUDINARY_UPLOAD_MARKER}${CLOUDINARY_VIDEO_DELIVERY}/`,
        CLOUDINARY_UPLOAD_MARKER
    );

/**
 * Ask Cloudinary for the delivery rendition once, right after the upload,
 * so the transcode happens now — while the operator is still on the form —
 * instead of on the first shopper's tap. Best-effort: resolves "" when the
 * clip is ready or still cooking (Cloudinary answers 423 while it works),
 * and a human sentence when Cloudinary refuses to convert it at all (a
 * 4xx other than 423 — typically a source over the plan's on-the-fly
 * transformation size limit). Never throws.
 */
export const warmCloudinaryVideo = async (url) => {
    if (typeof url !== "string" || !url.includes(CLOUDINARY_UPLOAD_MARKER)) return "";
    if (typeof fetch !== "function") return "";
    try {
        const res = await fetch(url, { method: "HEAD", mode: "cors", cache: "no-store" });
        if (res.ok || res.status === 423 || res.status === 0) return "";
        if (res.status >= 400 && res.status < 500) {
            return (
                `The video uploaded, but Cloudinary could not convert it for playback ` +
                `(HTTP ${res.status}). Shoppers on Chrome and Android may see a blank ` +
                `player. Try a shorter or smaller clip, ideally an MP4 under 40 MB.`
            );
        }
        return "";
    } catch {
        return "";
    }
};

/* CLOUDINARY thumbnail per the contract: "/upload/" -> "/upload/so_0/"
   and the file extension -> .jpg (first video frame as a still). */
export const cloudinaryVideoThumbnail = (url) => {
    const value = rawCloudinaryVideoUrl(url);
    if (!value.includes("/upload/")) return "";
    const withFrame = value.replace("/upload/", "/upload/so_0/");
    return /\.[^/.?#]+$/.test(withFrame)
        ? withFrame.replace(/\.[^/.?#]+$/, ".jpg")
        : `${withFrame}.jpg`;
};

/**
 * Build a contract-shaped CLOUDINARY entry from an uploadVideoToCloudinary
 * result. Returns null if the result carries no secure_url.
 */
export const buildCloudinaryVideoEntry = (result) => {
    if (!result?.secure_url) return null;
    const durationSec = Number(result.durationSec ?? result.duration);
    return {
        kind: "CLOUDINARY",
        // The H.264 mp4 DELIVERY url, not the raw upload — see
        // playableCloudinaryVideoUrl. The thumbnail still comes off the raw url.
        url: playableCloudinaryVideoUrl(result.secure_url),
        publicId: result.public_id || "",
        thumbnailUrl: cloudinaryVideoThumbnail(result.secure_url),
        durationSec:
            Number.isFinite(durationSec) && durationSec >= 0
                ? Math.round(durationSec)
                : 0,
    };
};

/**
 * Build a contract-shaped YOUTUBE entry from any pasted YouTube URL form.
 * Returns null when the URL is not a recognisable single YouTube video.
 */
export const buildYouTubeVideoEntry = (url) => {
    const id = extractYouTubeId(url);
    if (!id) return null;
    return {
        kind: "YOUTUBE",
        url: `https://www.youtube.com/watch?v=${id}`,
        publicId: "",
        thumbnailUrl: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
        durationSec: 0,
    };
};

export default uploadVideoToCloudinary;
